import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { api, mediaDownloadUrl, type GenerationJobStatus, type VideoPlan, type VideoPlanShotInput, type VideoShot } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * A longer video, as the studio plans and runs it.
 *
 * Video models make clips of a few seconds and grow them only by extending
 * the last frame, so a longer video is a *plan*: the script cut into shots,
 * each described visually in English, reviewed before anything is paid for,
 * then generated one shot at a time — pausing after each so the person can
 * watch the film so far and redo the last shot rather than pay for a chain
 * built on a bad one.
 *
 * Two pieces live here: the small form that asks the SCRIPT connector to
 * write the shot list (`VideoPlanForm`) and the editor/runner for one plan
 * (`VideoPlanEditor`). Plans are listed inside the studio project they
 * belong to, not on a list of their own.
 */

/** Veo's own sizes, mirrored from the API so the form can say "≈ N shots" before asking. */
export const FIRST_SHOT_SECONDS = 8;
export const EXTENSION_SECONDS = 7;
export const MAX_SHOTS = 21;
export const MIN_TARGET_SECONDS = 4;
export const MAX_TARGET_SECONDS = FIRST_SHOT_SECONDS + EXTENSION_SECONDS * (MAX_SHOTS - 1);

export function shotCountFor(targetSeconds: number): number {
  if (!Number.isFinite(targetSeconds) || targetSeconds <= FIRST_SHOT_SECONDS) return 1;
  return Math.min(MAX_SHOTS, 1 + Math.ceil((targetSeconds - FIRST_SHOT_SECONDS) / EXTENSION_SECONDS));
}

const planQueryKey = (id: string) => ['video-plan', id] as const;
const plansQueryKey = ['video-plans'] as const;

/* -------------------------------------------------------------------------- */
/*  The form: ask for a shot list                                              */
/* -------------------------------------------------------------------------- */

export function VideoPlanForm({
  scriptJobId,
  scriptText,
  referenceMediaIds,
  ensureProjectId,
  onCreated,
  onCancel,
}: {
  /** Set when the script came from the studio's own script job. */
  scriptJobId: string | null;
  /** The pasted script, used when there is no script job. */
  scriptText: string;
  /** The Compose tab's image attachments; the first three shape the first shot. */
  referenceMediaIds: string[];
  /** Resolves to the studio project this plan joins, creating it if the request has none yet. */
  ensureProjectId?: () => Promise<string>;
  onCreated: (plan: VideoPlan) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [targetSeconds, setTargetSeconds] = useState(30);
  const [pauseBetweenShots, setPauseBetweenShots] = useState(true);

  const references = referenceMediaIds.slice(0, 3);
  const clamped = Math.min(MAX_TARGET_SECONDS, Math.max(MIN_TARGET_SECONDS, Math.round(targetSeconds || 0)));

  const create = useMutation({
    mutationFn: async () => {
      const projectId = await ensureProjectId?.();
      return api.createVideoPlan({
        ...(scriptJobId ? { scriptJobId } : { scriptText }),
        referenceMediaIds: references,
        targetSeconds: clamped,
        pauseBetweenShots,
        ...(projectId ? { projectId } : {}),
      });
    },
    onSuccess: (plan) => {
      queryClient.setQueryData(planQueryKey(plan.id), plan);
      void queryClient.invalidateQueries({ queryKey: plansQueryKey });
      void queryClient.invalidateQueries({ queryKey: ['generation-project'] });
      void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
      onCreated(plan);
    },
  });

  return (
    <div className="card video-plan-form">
      <p className="muted small" style={{ marginTop: 0 }}>
        {t('videoPlan.intro')}
      </p>

      <div className="inline-form">
        <label htmlFor="video-plan-seconds">{t('videoPlan.targetSeconds')}</label>
        <input
          id="video-plan-seconds"
          type="number"
          min={MIN_TARGET_SECONDS}
          max={MAX_TARGET_SECONDS}
          step={1}
          value={targetSeconds}
          disabled={create.isPending}
          onChange={(event) => setTargetSeconds(Number(event.target.value))}
          onBlur={() => setTargetSeconds(clamped)}
          style={{ width: 90 }}
        />
        <span className="muted small">{t('videoPlan.shotEstimate', { count: shotCountFor(clamped) })}</span>
      </div>

      <label className="check-row">
        <input
          type="checkbox"
          checked={pauseBetweenShots}
          disabled={create.isPending}
          onChange={(event) => setPauseBetweenShots(event.target.checked)}
        />
        <span className="tiny">{t('videoPlan.pauseBetweenShots')}</span>
      </label>

      <p className="muted small">
        {references.length > 0
          ? t('videoPlan.referenceNote', { count: references.length })
          : t('videoPlan.referenceNoteNone')}
      </p>

      {create.isPending ? (
        <p className="small">
          <Icon.refresh className="spin" /> {t('videoPlan.creating')}
        </p>
      ) : (
        <div className="actions" style={{ marginTop: 6 }}>
          <button type="button" className="primary" onClick={() => create.mutate()}>
            <Icon.sparkle />
            {t('videoPlan.create')}
          </button>
          <button type="button" className="ghost small" onClick={onCancel}>
            {t('videoPlan.cancel')}
          </button>
        </div>
      )}
      {create.isError && <ErrorNotice error={create.error} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  The editor: review, run, pause, redo                                       */
/* -------------------------------------------------------------------------- */

function isMoving(status: VideoPlan['status']): boolean {
  return status === 'RUNNING';
}

/**
 * Poll a plan while it runs. Five seconds rather than the job poll's two: a
 * shot takes minutes, and the server does the waiting on the provider.
 */
function usePlanPoll(planId: string, initial?: VideoPlan) {
  return useQuery({
    queryKey: planQueryKey(planId),
    queryFn: () => api.getVideoPlan(planId),
    initialData: initial,
    refetchInterval: (query) => (query.state.data && isMoving(query.state.data.status) ? 5_000 : false),
  });
}

function sameShots(a: VideoPlanShotInput[], b: VideoPlanShotInput[]): boolean {
  return (
    a.length === b.length &&
    a.every((shot, index) => shot.scriptExcerpt === b[index]?.scriptExcerpt && shot.visualPrompt === b[index]?.visualPrompt)
  );
}

function toInputs(shots: VideoShot[]): VideoPlanShotInput[] {
  return shots.map(({ scriptExcerpt, visualPrompt }) => ({ scriptExcerpt, visualPrompt }));
}

export function VideoPlanEditor({
  planId,
  initialPlan,
  onBack,
}: {
  planId: string;
  initialPlan?: VideoPlan;
  onBack?: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const query = usePlanPoll(planId, initialPlan);
  const plan = query.data;

  // Edits are local until "Save shots": a keystroke must not reset a shot
  // that already cost money. `draft` is null while it mirrors the server.
  const [draft, setDraft] = useState<VideoPlanShotInput[] | null>(null);
  const [redoShotId, setRedoShotId] = useState<string | null>(null);
  const [redoPrompt, setRedoPrompt] = useState('');
  const [sharedUrl, setSharedUrl] = useState<string | null>(null);

  // A plan that starts running elsewhere (or is reloaded) drops local edits:
  // the server's list is the one being generated.
  useEffect(() => {
    if (plan && isMoving(plan.status)) setDraft(null);
  }, [plan]);

  const settle = (next: VideoPlan) => {
    queryClient.setQueryData(planQueryKey(next.id), next);
    void queryClient.invalidateQueries({ queryKey: plansQueryKey });
    // The project page and archive card show this plan's state too.
    void queryClient.invalidateQueries({ queryKey: ['generation-project'] });
    void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
  };

  const save = useMutation({
    mutationFn: (shots: VideoPlanShotInput[]) => api.updateVideoPlan(planId, { shots }),
    onSuccess: (next) => {
      settle(next);
      setDraft(null);
    },
  });

  const setPause = useMutation({
    mutationFn: (pauseBetweenShots: boolean) => api.updateVideoPlan(planId, { pauseBetweenShots }),
    onSuccess: settle,
  });

  const run = useMutation({
    mutationFn: () => api.runVideoPlan(planId),
    onSuccess: (next) => {
      settle(next);
      setSharedUrl(null);
    },
  });

  // Redo = reset that shot with the new description, then run again: two
  // calls, one intention, so one button.
  const redo = useMutation({
    mutationFn: async ({ shotId, visualPrompt }: { shotId: string; visualPrompt?: string }) => {
      await api.resetVideoPlanShot(planId, shotId, visualPrompt ? { visualPrompt } : {});
      return api.runVideoPlan(planId);
    },
    onSuccess: (next) => {
      settle(next);
      setRedoShotId(null);
      setRedoPrompt('');
      setDraft(null);
      setSharedUrl(null);
    },
  });

  const share = useMutation({
    mutationFn: () => api.shareVideoPlan(planId),
    onSuccess: async (link) => {
      setSharedUrl(link.url);
      // Clipboard access can be refused (http, permissions); the link is
      // shown on screen either way, so a refusal costs nothing.
      try {
        await navigator.clipboard.writeText(link.url);
      } catch {
        /* shown below instead */
      }
    },
  });

  if (!plan) {
    if (query.isError) return <ErrorNotice error={query.error} />;
    return <div className="loading">{t('common.loading')}</div>;
  }

  const locked = isMoving(plan.status) || save.isPending || run.isPending || redo.isPending;
  const serverShots = toInputs(plan.shots);
  const shots = draft ?? serverShots;
  const dirty = draft !== null && !sameShots(draft, serverShots);
  const busy = save.isPending || run.isPending || redo.isPending;

  const editShot = (index: number, patch: Partial<VideoPlanShotInput>) =>
    setDraft((current) => (current ?? serverShots).map((shot, i) => (i === index ? { ...shot, ...patch } : shot)));
  const removeLast = () =>
    setDraft((current) => {
      const list = current ?? serverShots;
      return list.length > 1 ? list.slice(0, -1) : list;
    });
  const addShot = () =>
    setDraft((current) => {
      const list = current ?? serverShots;
      return list.length < MAX_SHOTS ? [...list, { scriptExcerpt: '', visualPrompt: '' }] : list;
    });

  const runningIndex = plan.shots.findIndex((shot) => shot.status === 'RUNNING');
  const lastDone = [...plan.shots].reverse().find((shot) => shot.status === 'DONE');
  // A failed plan usually has a FAILED shot; when it stopped before one
  // started (a missing reference to extend from), the next pending shot is
  // the one to fix and retry.
  const failedShot =
    plan.status === 'FAILED'
      ? (plan.shots.find((shot) => shot.status === 'FAILED') ?? plan.shots.find((shot) => shot.status !== 'DONE'))
      : undefined;
  const canSave = dirty && shots.every((shot) => shot.visualPrompt.trim().length > 0);

  const startPlan = () => {
    if (window.confirm(t('videoPlan.confirmStart', { count: plan.shots.length }))) run.mutate();
  };
  const continuePlan = () => {
    if (window.confirm(t('videoPlan.confirmContinue'))) run.mutate();
  };
  const openRedo = (shot: VideoShot) => {
    setRedoShotId(shot.id);
    setRedoPrompt(shot.visualPrompt);
  };

  const totalSeconds = shots.reduce(
    (sum, _shot, index) => sum + (index === 0 ? FIRST_SHOT_SECONDS : EXTENSION_SECONDS),
    0,
  );

  return (
    <article className="card video-plan">
      <header className="card-head">
        <div>
          <strong>{t('videoPlan.title')}</strong>{' '}
          <span className="muted small">{t('videoPlan.summary', { seconds: totalSeconds, count: shots.length })}</span>
        </div>
        <span className={`badge ${planStatusClass(plan.status)}`}>
          {t(`videoPlan.status.${plan.status}` as TranslationKey)}
        </span>
      </header>

      {onBack && (
        <button type="button" className="ghost small" onClick={onBack} style={{ marginBottom: 8 }}>
          {t('videoPlan.backToList')}
        </button>
      )}

      {plan.status === 'RUNNING' && (
        <p className="small">
          <Icon.refresh className="spin" />{' '}
          {t('videoPlan.runningNow', {
            number: (runningIndex === -1 ? plan.shots.findIndex((s) => s.status !== 'DONE') : runningIndex) + 1,
            count: plan.shots.length,
          })}
        </p>
      )}
      {plan.status === 'PAUSED' && <p className="muted small">{t('videoPlan.pausedHint')}</p>}
      {plan.status === 'DONE' && <p className="muted small">{t('videoPlan.doneHint')}</p>}
      {plan.status === 'FAILED' && (
        <p className="error small">{plan.errorMessage ?? t('studio.jobFailed')}</p>
      )}

      {/* The film so far. Each extension returns the whole video to date,
          so one player is always enough. */}
      {plan.resultMediaId ? (
        <>
          <p className="small" style={{ marginBottom: 0 }}>
            {plan.status === 'DONE' ? t('videoPlan.finalVideo') : t('videoPlan.videoSoFar')}
          </p>
          <video
            controls
            key={plan.resultMediaId}
            src={`/api/media/${plan.resultMediaId}`}
            className="studio-result-media"
          />
          <p className="small" style={{ marginTop: 0 }}>
            <a className="download-link" href={mediaDownloadUrl(plan.resultMediaId)} download>
              <Icon.download />
              {t('studio.download')}
            </a>
          </p>
        </>
      ) : (
        plan.status === 'DRAFT' && <p className="muted small">{t('videoPlan.noVideoYet')}</p>
      )}

      {isMoving(plan.status) && <p className="muted small">{t('videoPlan.editingLocked')}</p>}

      <ol className="video-plan-shots">
        {shots.map((shot, index) => {
          const saved = plan.shots[index];
          const from = index === 0 ? 0 : FIRST_SHOT_SECONDS + EXTENSION_SECONDS * (index - 1);
          const to = from + (index === 0 ? FIRST_SHOT_SECONDS : EXTENSION_SECONDS);
          const changed =
            !saved || saved.visualPrompt !== shot.visualPrompt || saved.scriptExcerpt !== shot.scriptExcerpt;
          const shotStatus: GenerationJobStatus = changed ? 'PENDING' : (saved?.status ?? 'PENDING');
          const isRunning = saved?.status === 'RUNNING' && !changed;
          const textareaId = `video-plan-shot-${plan.id}-${index}`;
          return (
            <li key={saved?.id ?? `new-${index}`} className={`card video-plan-shot ${isRunning ? 'running' : ''}`}>
              <header className="card-head">
                <div>
                  <strong>{t('videoPlan.shot', { number: index + 1 })}</strong>{' '}
                  <span className="mono muted small" dir="ltr">{t('videoPlan.timeRange', { from, to })}</span>
                </div>
                <span className={`badge ${shotStatusClass(shotStatus)}`}>
                  {isRunning && <Icon.refresh className="spin" />}{' '}
                  {t(`videoPlan.shotStatus.${shotStatus}` as TranslationKey)}
                </span>
              </header>

              {shot.scriptExcerpt && (
                <p className="muted small text-cell video-plan-excerpt" title={t('videoPlan.scriptExcerpt')}>
                  {shot.scriptExcerpt}
                </p>
              )}

              <label htmlFor={textareaId} className="small">
                {t('videoPlan.visualPrompt')}
              </label>
              <textarea
                id={textareaId}
                dir="ltr"
                className="ltr"
                rows={3}
                value={shot.visualPrompt}
                disabled={locked || redoShotId !== null}
                onChange={(event) => editShot(index, { visualPrompt: event.target.value })}
              />
              <p className="tiny muted">{t('videoPlan.visualPromptHint')}</p>

              {saved?.status === 'FAILED' && !changed && saved.errorMessage && (
                <p className="error small">{saved.errorMessage}</p>
              )}

              <div className="actions" style={{ marginTop: 6 }}>
                {/* Only the newest finished shot can be redone from the
                    pause: an earlier one would throw away every shot after
                    it, which is what editing and saving is for. */}
                {(plan.status === 'PAUSED' || plan.status === 'FAILED') &&
                  saved &&
                  lastDone?.id === saved.id &&
                  !dirty &&
                  redoShotId === null && (
                  <button type="button" className="ghost small" onClick={() => openRedo(saved)} disabled={busy}>
                    <Icon.refresh />
                    {t('videoPlan.redoShot')}
                  </button>
                )}
                {plan.status === 'FAILED' && saved && failedShot?.id === saved.id && !dirty && redoShotId === null && (
                  <button type="button" className="ghost small" onClick={() => openRedo(saved)} disabled={busy}>
                    <Icon.refresh />
                    {t('videoPlan.fixAndRetry')}
                  </button>
                )}
                {index === shots.length - 1 && shots.length > 1 && !locked && redoShotId === null && (
                  <button type="button" className="ghost small" onClick={removeLast}>
                    <Icon.trash />
                    {t('videoPlan.removeShot')}
                  </button>
                )}
              </div>

              {saved && redoShotId === saved.id && (
                <div className="inline-form video-plan-redo" style={{ marginTop: 10 }}>
                  <p className="muted small" style={{ margin: 0, flexBasis: '100%' }}>
                    {saved.status === 'DONE' ? t('videoPlan.redoHint') : t('videoPlan.fixHint')}
                  </p>
                  <textarea
                    dir="ltr"
                    className="ltr"
                    rows={3}
                    value={redoPrompt}
                    disabled={redo.isPending}
                    onChange={(event) => setRedoPrompt(event.target.value)}
                    style={{ flexBasis: '100%' }}
                  />
                  <button
                    type="button"
                    className="primary"
                    disabled={!redoPrompt.trim() || redo.isPending}
                    onClick={() => {
                      if (window.confirm(t('videoPlan.confirmContinue'))) {
                        redo.mutate({
                          shotId: saved.id,
                          visualPrompt: redoPrompt.trim() === saved.visualPrompt ? undefined : redoPrompt.trim(),
                        });
                      }
                    }}
                  >
                    {redo.isPending ? <Icon.refresh className="spin" /> : <Icon.sparkle />}
                    {saved.status === 'DONE' ? t('videoPlan.redoAndRun') : t('videoPlan.retry')}
                  </button>
                  <button
                    type="button"
                    className="ghost small"
                    disabled={redo.isPending}
                    onClick={() => {
                      setRedoShotId(null);
                      setRedoPrompt('');
                    }}
                  >
                    {t('videoPlan.cancel')}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {!locked && redoShotId === null && (
        <div className="actions" style={{ marginTop: 4 }}>
          {shots.length < MAX_SHOTS && (
            <button type="button" className="ghost small" onClick={addShot}>
              <Icon.plus />
              {t('videoPlan.addShot')}
            </button>
          )}
        </div>
      )}

      {dirty && (
        <>
          <p className="muted small">
            {t('videoPlan.unsavedChanges')} {t('videoPlan.saveNote')}
          </p>
          <div className="actions" style={{ marginTop: 4 }}>
            <button type="button" className="primary" onClick={() => save.mutate(shots)} disabled={!canSave || busy}>
              <Icon.check />
              {t('videoPlan.saveShots')}
            </button>
            <button type="button" className="ghost small" onClick={() => setDraft(null)} disabled={busy}>
              {t('videoPlan.discardChanges')}
            </button>
          </div>
        </>
      )}
      {save.isError && <ErrorNotice error={save.error} />}

      {!isMoving(plan.status) && (
        <label className="check-row">
          <input
            type="checkbox"
            checked={plan.pauseBetweenShots}
            disabled={busy || setPause.isPending}
            onChange={(event) => setPause.mutate(event.target.checked)}
          />
          <span className="tiny">{t('videoPlan.pauseBetweenShots')}</span>
        </label>
      )}
      {setPause.isError && <ErrorNotice error={setPause.error} />}

      {!dirty && redoShotId === null && (
        <div className="actions">
          {plan.status === 'DRAFT' && (
            <button type="button" className="primary" onClick={startPlan} disabled={busy}>
              <Icon.sparkle />
              {t('videoPlan.approveAndStart')}
            </button>
          )}
          {plan.status === 'PAUSED' && (
            <button type="button" className="primary" onClick={continuePlan} disabled={busy}>
              <Icon.sparkle />
              {t('videoPlan.continue')}
            </button>
          )}
          {plan.status === 'FAILED' && failedShot && (
            <button type="button" onClick={() => openRedo(failedShot)} disabled={busy}>
              <Icon.refresh />
              {t('videoPlan.fixAndRetry')}
            </button>
          )}
          {plan.resultMediaId && (
            <button type="button" className="ghost small" onClick={() => share.mutate()} disabled={share.isPending}>
              <Icon.link />
              {t('videoPlan.copyLink')}
            </button>
          )}
        </div>
      )}
      {run.isError && <ErrorNotice error={run.error} />}
      {redo.isError && <ErrorNotice error={redo.error} />}
      {share.isError && <ErrorNotice error={share.error} />}

      {sharedUrl && (
        <p className="small">
          {t('studio.linkReady')}{' '}
          <a className="mono ltr" dir="ltr" href={sharedUrl} target="_blank" rel="noreferrer">
            {sharedUrl}
          </a>
        </p>
      )}
    </article>
  );
}

function planStatusClass(status: VideoPlan['status']): string {
  if (status === 'DONE') return 'ok';
  if (status === 'FAILED') return 'err';
  if (status === 'RUNNING') return 'accent';
  return 'warn';
}

function shotStatusClass(status: GenerationJobStatus): string {
  if (status === 'APPROVED' || status === 'DONE') return 'ok';
  if (status === 'FAILED') return 'err';
  if (status === 'RUNNING') return 'accent';
  return 'warn';
}
